import { getSearchTemplateById } from '@/apis/searchtemplates';
import { useSearchPreviewQuery } from '@/apis/searchTree';
import { NotRenewingContext } from '@/App';
import { microfrontendURLPrefix } from '@/config';
import { useFeatureToggle } from '@/featureToggles/FeatureTogglesContext';
import { usePrecacheUserContactPerms, useUserDetails } from '@/hooks';
import { useSearchPreviewsPaused } from '@/hooks/useSearchPreviewsPaused';
import { SearchTemplate } from '@/types';
import { SearchTreeNode } from '@/types/searchTreeTypes';
import { hasNotRenewing } from '@/util';
import { navBarOpenAtom } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import type { SearchMetadata } from '@components/SaveSearchDialog';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { searchTemplateDataAtom } from '@/state/searchTemplate/searchTemplateData';
import { useSentryTags } from '@hooks/useSentryTags';
import { useUnsavedChangesBlocker } from '@hooks/useUnsavedChangesBlocker';
import useQueryBuilderConfig from './queryBuilderConfig';
import {
  checkIsSearchTreeIncomplete,
  mapToSearchTree,
  SearchTreeBuilderState,
} from './SearchTreeBuilder';
import { isValidationDialogOpenAtom, templateValidationErrorsAtom } from './ValidationDialog';
import type { AdvancedSearchListCreatorPageProps, SaveSearchDialogStatus } from './advancedSearchListCreatorPageTypes';
import { searchTreeIsEmpty } from './searchTreeIsEmpty';
import { useApplyLoadedSearchTemplate } from './useApplyLoadedSearchTemplate';
import {
  advancedSearchDateFilterInvalidAtom,
  debugAtom,
  searchTreeBuilderStateAtom,
} from './stateAtoms';

export function useAdvancedSearchListCreatorPage({
  loadedTree,
  loadedMetadata,
}: Pick<AdvancedSearchListCreatorPageProps, 'loadedTree' | 'loadedMetadata'>) {
  const enableDevFeatures = useFeatureToggle('Developer Features');
  const { getAccessTokenSilently } = useAuth0();
  const [debug, setDebug] = useAtom(debugAtom);
  const searchTemplateData = useAtomValue(searchTemplateDataAtom);
  const templateValidationErrors = useAtomValue(templateValidationErrorsAtom);
  const hasTemplateErrors = searchTemplateData !== undefined && templateValidationErrors.length > 0;
  const { searchId } = useParams();
  const { data: userDetails } = useUserDetails();
  const navigate = useNavigate();
  const { setNotRenewingPresent } = useContext(NotRenewingContext);
  usePrecacheUserContactPerms();
  useSentryTags({ 'search-type': 'advanced', 'search-id': searchId });
  const config = useQueryBuilderConfig();
  const [searchMetadata, setSearchMetadata] = useState(loadedMetadata);
  const [searchTreeBuilderState, setSearchTreeBuilderState] = useState<SearchTreeBuilderState>({
    tree: loadedTree,
    config,
  });
  const initialLoadedSearchTree = mapToSearchTree(loadedTree, config, {});
  const [builderSearchTree, setBuilderSearchTree] = useState(initialLoadedSearchTree);
  const [activeSearchTree, setActiveSearchTree] = useState<SearchTreeNode | undefined>(undefined);
  const [expandCriteriaAccordion, setExpandCriteriaAccordion] = useState(
    searchTemplateData !== undefined && !hasTemplateErrors ? false : true
  );
  const [isOtherActionsPanelOpen, setIsOtherActionsPanelOpen] = useState(false);
  const [isFilterIncomplete, setIsFilterIncomplete] = useState(checkIsSearchTreeIncomplete(loadedTree));
  const [saveSearchDialogStatus, setSaveSearchDialogStatus] = useState<SaveSearchDialogStatus>('closed');
  const [isUpdateSearchDialogOpen, setIsUpdateSearchDialogOpen] = useState(false);
  const [isSearchModified, setIsSearchModified] = useState(false);
  const { blocker, popStateBlock, allowNextNavigation } =
    useUnsavedChangesBlocker(isSearchModified);
  const [treeChangedSinceLastPreview, setTreeChangedSinceLastPreview] = useState(loadedMetadata.id !== undefined);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [isSaveListDialogOpen, setIsSaveListDialogOpen] = useState(false);
  const [isTemplateWarningModalOpen, setIsTemplateWarningModalOpen] = useState(hasTemplateErrors);
  const previewsPaused = useSearchPreviewsPaused();
  const [restartTemplateForLoad, setRestartTemplateForLoad] = useState<SearchTemplate | undefined>(undefined);
  const [isEditCriteriaModalOpen, setIsEditCriteriaModalOpen] = useState(false);
  const [hasProceededFromEditCriteria, setHasProceededFromEditCriteria] = useState(false);
  const preEditSearchTreeBuilderStateRef = useRef<SearchTreeBuilderState | null>(null);
  const preEditBuilderSearchTreeRef = useRef<SearchTreeNode | undefined>(undefined);
  const preEditTreeChangedSinceLastPreviewRef = useRef<boolean>(false);
  const restartFromEditCriteriaRef = useRef(false);
  const isRevertingEditCriteriaRef = useRef(false);
  const queryClient = useQueryClient();
  const { data: listPreview, isFetching: isPreviewFetching } =
    useSearchPreviewQuery(activeSearchTree);
  const setIsNavbarOpen = useSetAtom(navBarOpenAtom);
  const setSearchTreeBuilderStateForTemplatePage = useSetAtom(searchTreeBuilderStateAtom);

  useEffect(() => {
    setIsNavbarOpen(false);
  }, [setIsNavbarOpen]);

  const handleApply = useCallback(async () => {
    if (previewsPaused) return;
    setTreeChangedSinceLastPreview(false);
    setActiveSearchTree(builderSearchTree ?? initialLoadedSearchTree);
    setSelectedContactIds([]);
  }, [builderSearchTree, initialLoadedSearchTree, previewsPaused]);

  const handleCancel = useCallback(() => {
    // TODO [LIST-646] This just cancels the request from the client's POV, the backend will keep processing it
    queryClient.cancelQueries({ queryKey: ['advsearch', 'preview'] });
    queryClient.invalidateQueries({ queryKey: ['advsearch', 'preview'] });
    // Importantly, we need to clear the active tree, otherwise it will go again
    setActiveSearchTree(undefined);
  }, [queryClient]);

  const applySearchTreeChange = useCallback(
    (newSearchTree: SearchTreeNode | undefined, isIncomplete: boolean) => {
      setIsFilterIncomplete(isIncomplete);
      setIsSearchModified(true);
      setTreeChangedSinceLastPreview(true);
      setBuilderSearchTree(newSearchTree);
      const isNotRenewing = hasNotRenewing(newSearchTree);
      setNotRenewingPresent(isNotRenewing);
    },
    [setNotRenewingPresent]
  );

  const revertEditCriteriaChange = useCallback(() => {
    isRevertingEditCriteriaRef.current = true;
    if (preEditSearchTreeBuilderStateRef.current) {
      setSearchTreeBuilderState(preEditSearchTreeBuilderStateRef.current);
    }
    if (preEditBuilderSearchTreeRef.current !== undefined) {
      setBuilderSearchTree(preEditBuilderSearchTreeRef.current);
      setIsFilterIncomplete(
        checkIsSearchTreeIncomplete(preEditSearchTreeBuilderStateRef.current?.tree)
      );
      setNotRenewingPresent(hasNotRenewing(preEditBuilderSearchTreeRef.current));
    }
    setIsSearchModified(false);
    setTreeChangedSinceLastPreview(preEditTreeChangedSinceLastPreviewRef.current);
  }, [setNotRenewingPresent]);

  const handleBuilderSearchTreeChange = useCallback(
    (newSearchTree: SearchTreeNode | undefined, isIncomplete: boolean) => {
      if (isRevertingEditCriteriaRef.current) {
        isRevertingEditCriteriaRef.current = false;
        return;
      }

      const shouldShowEditCriteriaModal =
        searchTemplateData !== undefined && !hasProceededFromEditCriteria && !isTemplateWarningModalOpen;

      if (shouldShowEditCriteriaModal) {
        preEditSearchTreeBuilderStateRef.current = searchTreeBuilderState;
        preEditBuilderSearchTreeRef.current = builderSearchTree;
        preEditTreeChangedSinceLastPreviewRef.current = treeChangedSinceLastPreview;
        applySearchTreeChange(newSearchTree, isIncomplete);
        setIsEditCriteriaModalOpen(true);
        return;
      }

      applySearchTreeChange(newSearchTree, isIncomplete);
    },
    [applySearchTreeChange, builderSearchTree, hasProceededFromEditCriteria, isTemplateWarningModalOpen, searchTemplateData, searchTreeBuilderState, treeChangedSinceLastPreview]
  );

  const handleSaveSearchDialogOpen = useCallback(() => {
    if (isSearchModified) {
      if (searchMetadata.id !== undefined && searchTemplateData === undefined) {
        setIsUpdateSearchDialogOpen(true);
      } else {
        setSaveSearchDialogStatus('open');
      }
    } else {
      if (searchTemplateData === undefined) {
        setSaveSearchDialogStatus('prefilled');
      } else {
        setSaveSearchDialogStatus('open');
      }
    }
  }, [isSearchModified, searchMetadata.id, searchTemplateData]);

  const navigateToTemplatePage = useCallback(() => {
    setSearchTreeBuilderStateForTemplatePage(searchTreeBuilderState);
    navigate(`${microfrontendURLPrefix}/template`);
  }, [navigate, searchTreeBuilderState, setSearchTreeBuilderStateForTemplatePage]);

  const handleSaveSearchDialogClose = useCallback(
    (metadata?: SearchMetadata) => {
      if (metadata) {
        setSearchMetadata(metadata);
        setIsSearchModified(false);
        if (metadata.id != null) {
          navigate(`${microfrontendURLPrefix}/advancedsearch/${metadata.id}`);
        }
      }
      setSaveSearchDialogStatus('closed');
    },
    [navigate]
  );

  const isBuilderSearchTreeEmpty =
    searchTreeIsEmpty(builderSearchTree) && searchTreeIsEmpty(initialLoadedSearchTree);

  const dateFilterInvalidMap = useAtomValue(advancedSearchDateFilterInvalidAtom);
  const isDateFilterInvalid = Object.values(dateFilterInvalidMap).some(Boolean);

  const isApplyDisabled =
    previewsPaused ||
    isFilterIncomplete ||
    isDateFilterInvalid ||
    isBuilderSearchTreeEmpty ||
    isPreviewFetching ||
    !treeChangedSinceLastPreview ||
    isTemplateWarningModalOpen ||
    isEditCriteriaModalOpen;

  const { initialSaveSearchDisabled } = useApplyLoadedSearchTemplate({
    searchTemplateData,
    hasTemplateErrors,
    handleApply,
    isPreviewFetching,
    previewsPaused,
  });

  const isSaveSearchDisabled =
    activeSearchTree === undefined ||
    treeChangedSinceLastPreview ||
    isBuilderSearchTreeEmpty ||
    (searchTemplateData !== undefined && isPreviewFetching && !initialSaveSearchDisabled);

  const isValidationDialogOpen = useAtomValue(isValidationDialogOpenAtom);

  useEffect(() => {
    if (isPreviewFetching === false) {
      const returnedDataIds = listPreview ? listPreview.entities.map((x) => x.id) : [];
      const filteredContactIds = selectedContactIds.filter((value) =>
        returnedDataIds.includes(value)
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-derived-state -- drop selected ids that are no longer in the preview
      setSelectedContactIds(filteredContactIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewFetching]);

  const previewCount = listPreview?.count ?? 0;
  const areBothButtonsDisabled =
    previewCount === 0 || isPreviewFetching || treeChangedSinceLastPreview;

  const handleClickOtherActions = useCallback(() => {
    setIsOtherActionsPanelOpen(true);
  }, []);

  const handleClickSaveToList = useCallback(() => {
    setIsSaveListDialogOpen(true);
  }, []);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- keep restart handler identity stable
  const handleRestartClick = useCallback(async () => {
    if (searchTemplateData?.templateId == null) return;
    const token = await getAccessTokenSilently();
    const template = await getSearchTemplateById(token, searchTemplateData.templateId);
    setRestartTemplateForLoad(template);
  }, [getAccessTokenSilently, searchTemplateData?.templateId]);

  const showRestartButton =
    searchTemplateData !== undefined && searchTemplateData.templateId != null;

  const toggleCriteriaAccordion = useCallback(() => {
    setExpandCriteriaAccordion((prev) => !prev);
  }, []);

  const asteriskIndicatingMod = isSearchModified && searchMetadata.name !== undefined ? '*' : '';

  return {
    enableDevFeatures, debug, setDebug, userDetails,
    searchTemplateData, hasTemplateErrors,
    searchMetadata, asteriskIndicatingMod, navigateToTemplatePage,
    isTemplateWarningModalOpen, setIsTemplateWarningModalOpen,
    isEditCriteriaModalOpen, setIsEditCriteriaModalOpen, hasProceededFromEditCriteria, setHasProceededFromEditCriteria,
    isValidationDialogOpen,
    expandCriteriaAccordion, toggleCriteriaAccordion,
    searchTreeBuilderState, setSearchTreeBuilderState, handleBuilderSearchTreeChange,
    isPreviewFetching, handleCancel, showRestartButton, handleRestartClick,
    isFilterIncomplete, isApplyDisabled, handleApply,
    isSaveSearchDisabled, handleSaveSearchDialogOpen,
    saveSearchDialogStatus, handleSaveSearchDialogClose, isUpdateSearchDialogOpen, setIsUpdateSearchDialogOpen,
    setSaveSearchDialogStatus, setIsSearchModified,
    blocker, popStateBlock, allowNextNavigation,
    listPreview, activeSearchTree, builderSearchTree,
    selectedContactIds, setSelectedContactIds, sortModel, setSortModel, filterModel, setFilterModel, isSaveListDialogOpen, setIsSaveListDialogOpen,
    areBothButtonsDisabled, isOtherActionsPanelOpen, setIsOtherActionsPanelOpen, handleClickOtherActions, handleClickSaveToList,
    restartTemplateForLoad, setRestartTemplateForLoad, restartFromEditCriteriaRef, revertEditCriteriaChange,
  };
}

